

import { ComponentProps, FC, PropsWithChildren } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Box, Ribbon as RibbonPrimitive, Popover, TextLink, Text, Stack } from '@eneco/sparky';
import { GiftIcon, InfoIcon } from '@eneco/sparky/icons';
import { DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

type RibbonProps = Pick<ComponentProps<typeof RibbonPrimitive>, 'size' | 'emphasis'>;

type ContentContainer = 'popover' | 'dialog';
interface Props extends PropsWithChildren {
  theme?: DC_Repositories_Base_Enumerations_Label;
  size?: RibbonProps['size'];
  emphasis?: RibbonProps['emphasis'];

  title: string;
  text?: string;
  trigger?: string;
  contentContainer?: ContentContainer;
  ribbonTitle?: string;
  hasGiftIcon?: boolean;
  hasInfoIcon?: boolean;
}

type RibbonContentProps = Pick<
  Props,
  'trigger' | 'text' | 'title' | 'size' | 'hasInfoIcon' | 'contentContainer' | 'children'
>;

const RibbonContent: FC<RibbonContentProps> = ({
  trigger,
  text,
  title,
  size,
  hasInfoIcon,
  contentContainer = 'popover',
  children,
}) => {
  if (!trigger || !text) {
    return null;
  }
  const Container = contentContainer === 'popover' ? Popover : TrackedDialog;
  return (
    <Container
      side="bottom"
      title={title}
      trigger={
        <TextLink
          leftIcon={
            hasInfoIcon ? (
              <InfoIcon size="small" verticalAlign={size === 'large' ? 'middle' : 'bottom'} color={'currentColor'} />
            ) : undefined
          }>
          {trigger}
        </TextLink>
      }>
      <RichText html={text} />
      {children}
    </Container>
  );
};

export const Ribbon: FC<Props> = ({
  theme = 'eneco',
  size = 'large',
  emphasis,
  title,
  text,
  trigger,
  contentContainer,
  ribbonTitle,
  hasGiftIcon = true,
  hasInfoIcon = true,
  children,
}) => (
  <RibbonPrimitive emphasis={emphasis} size={size}>
    <Stack gap="2" alignX="center" alignY="center" direction="row">
      {hasGiftIcon && (
        <GiftIcon verticalAlign={'bottom'} color="currentColor" size={size === 'small' ? 'small' : 'medium'} />
      )}

      <Stack.Item>
        <Box paddingTop={theme === 'eneco' ? 1 : 0}>
          <Text weight="bold" size={size === 'small' ? {} : { initial: 'BodyXS', sm: 'BodyS' }}>
            <RichText html={ribbonTitle || title} />{' '}
            <RibbonContent
              trigger={trigger}
              text={text}
              title={title}
              size={size}
              contentContainer={contentContainer}
              hasInfoIcon={hasInfoIcon}>
              {children}
            </RibbonContent>
          </Text>
        </Box>
      </Stack.Item>
    </Stack>
  </RibbonPrimitive>
);
